import React, { useState, useEffect } from "react";
import { Accordion, Table, Button, Spinner, Alert } from "react-bootstrap";
import { Bar, Doughnut } from "react-chartjs-2";
import { getUserRoles, exportProcessedDataToExcel, exportPercentileSourceDataToExcel, downloadPercentileSourceDataCsvForGrade, retranslateReportGrades, recalculatePercentiles, triggerDataSync } from "../services/api";

// Remchannel Colour Palette
const REMCHANNEL_COLORS = {
  steelBlue: "#003366",
  green: "#00A651",
  black: "#000000",
  paper: "#FFFFFF",
  orange: "#FF6B00",
  rust: "#C35214",
  blue: "#1B3664",
  grey: "#666666",
  // Tints/Shades
  steelBlue80: "#33587A",
  steelBlue60: "#668BAF",
  steelBlue40: "#99BFD4",
  steelBlue20: "#CCDFF0",
  green80: "#33B86B",
  green60: "#66CB8C",
  green40: "#99DEAD",
  green20: "#CCF1CE",
  orange80: "#FF8A33",
  orange60: "#FFA866",
  orange40: "#FFC699",
  orange20: "#FFE4CC",
  rust80: "#D16B33",
  rust60: "#E18566",
  rust40: "#F1A099",
  rust20: "#F9CABB",
  blue80: "#495A7A",
  blue60: "#7A8BAF",
  blue40: "#AABFD4",
  blue20: "#D5DFF0",
  grey80: "#888888",
  grey60: "#AAAAAA",
  grey40: "#CCCCCC",
  grey20: "#EEEEEE",
  // Other high-contrast colors (distinct from palette above)
  contrastAqua: "#00FFFF",
  contrastMagenta: "#FF00FF",
  contrastLime: "#32CD32",
  contrastCoral: "#FF6347",
  contrastNavy: "#00008B",
  contrastGold: "#FFD700",
};

/** Known acronym/abbreviation overrides for proper casing */
const LEGEND_LABEL_OVERRIDES = {
  'fsp': 'FSP',
  'sars': 'SARs',
};

/** Format legend/label text: remove underscores, title-case each word */
const formatLegendLabel = (str) => {
  if (str == null || typeof str !== "string") return str ?? "";
  return str
    .replace(/_/g, " ")
    .split(" ")
    .map(word => LEGEND_LABEL_OVERRIDES[word.toLowerCase()] || (word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
};

/**
 * Parse hidden sections configuration from environment variable
 * Returns an object with boolean flags for each section
 * Supports both build-time (process.env) and runtime (window.ENV) configuration
 */
const getHiddenSections = () => {
  // Try runtime config first (window.ENV), then build-time config (process.env)
  const hiddenSectionsEnv = window.ENV?.REPORT_EXPORT_HIDDEN_SECTIONS 
    || process.env.REACT_APP_REPORT_EXPORT_HIDDEN_SECTIONS 
    || '{}';
  try {
    const config = typeof hiddenSectionsEnv === 'string' ? JSON.parse(hiddenSectionsEnv) : hiddenSectionsEnv;
    // Default to true (hidden) unless explicitly set to false
    return {
      ltiParticipationBaseSalary: config.ltiParticipationBaseSalary !== false,
      ltiParticipationTGP: config.ltiParticipationTGP !== false,
      ltiAnnualAwards: config.ltiAnnualAwards !== false,
      ltiAllocationPercentagesByGrade: config.ltiAllocationPercentagesByGrade !== false,
      stiParticipationBaseSalary: config.stiParticipationBaseSalary !== false,
      stiParticipationTGP: config.stiParticipationTGP !== false,
      stiAllocationPercentagesByRemunerationBase: config.stiAllocationPercentagesByRemunerationBase !== false,
      stiAllocationPercentagesByGrade: config.stiAllocationPercentagesByGrade !== false,
      totalRemunerationPayMixOldVersion: config.totalRemunerationPayMixOldVersion !== false,
    };
  } catch (error) {
    console.warn('Failed to parse REPORT_EXPORT_HIDDEN_SECTIONS, using defaults (all sections hidden):', error.message);
    // Default to true (hidden) for all sections
    return {
      ltiParticipationBaseSalary: true,
      ltiParticipationTGP: true,
      ltiAnnualAwards: true,
      ltiAllocationPercentagesByGrade: true,
      stiParticipationBaseSalary: true,
      stiParticipationTGP: true,
      stiAllocationPercentagesByRemunerationBase: true,
      stiAllocationPercentagesByGrade: true,
      totalRemunerationPayMixOldVersion: true,
    };
  }
};

const ReportSections = ({ reportData, reportId, circleId, circleName, gradingSystemId, gradingSystemName, organizationId, onReportRefresh }) => {
  const [hasAllowedRole, setHasAllowedRole] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPercentileData, setExportingPercentileData] = useState(false);
  const [exportingGradeCsv, setExportingGradeCsv] = useState(null);
  const [retranslating, setRetranslating] = useState(false);
  const [recalculatingPercentiles, setRecalculatingPercentiles] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  const orgId = localStorage.getItem("orgId");
  const isEmbedded = window.self !== window.top;
  
  // Get hidden sections configuration
  const hiddenSections = getHiddenSections();

  useEffect(() => {
    const checkUserRoles = async () => {
      if (orgId) {
        try {
          const roles = await getUserRoles();
          // Only apply role restrictions in embedded mode
          if (isEmbedded) {
            const allowedRoles = [1, 2, 3, 4, 5];
            const hasRole = roles.some((role) =>
              allowedRoles.includes(role.RoleID)
            );
            setHasAllowedRole(hasRole);
          } else {
            // In non-embedded mode, always allow access
            setHasAllowedRole(true);
          }
        } catch (error) {
          console.error("Error checking user roles:", error);
        }
      }
    };

    checkUserRoles();
  }, [orgId, isEmbedded]);

  if (!reportData) {
    return (
      <Accordion
        className="mt-4"
        id="report-sections-content"
        defaultActiveKey={["0"]}
      >
        <Accordion.Item eventKey="0">
          <Accordion.Header>NO DATA AVAILABLE</Accordion.Header>
          <Accordion.Body>
            <p>
              Please wait for the report data to load or try regenerating the
              report.
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  }

  // Color arrays for charts
  const ltiEligibilityColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.blue,
    REMCHANNEL_COLORS.grey,
  ];
  const ltiPlansColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.blue,
    REMCHANNEL_COLORS.grey,
  ];
  const ltiPerformanceConditionsColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.blue,
    REMCHANNEL_COLORS.grey,
  ];
  const ltiVestingMethodologyColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.blue,
    REMCHANNEL_COLORS.grey,
  ];
  const ltiVestingPeriodsColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.contrastMagenta,
    REMCHANNEL_COLORS.grey,
  ];
  const ltiSettlementMethodologyColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.blue,
    REMCHANNEL_COLORS.grey,
  ];
  const stiEligibilityColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.blue,
    REMCHANNEL_COLORS.grey,
  ];
  const stiPerformanceAssessmentColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.contrastGold,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.grey,
  ];
  const stiPlanTypesPerformanceConditionsColors = [
    REMCHANNEL_COLORS.steelBlue,
    REMCHANNEL_COLORS.green,
    REMCHANNEL_COLORS.orange,
    REMCHANNEL_COLORS.contrastGold,
    REMCHANNEL_COLORS.rust,
    REMCHANNEL_COLORS.grey,
    REMCHANNEL_COLORS.contrastMagenta,
  ];
  return (
    <Accordion
      className="mt-4"
      id="report-sections-content"
      defaultActiveKey={["0", "1", "2", "3"]}
      alwaysOpen
    >
      <Accordion.Item eventKey="0">
        <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
          INTRODUCTION AND METHODOLOGY
        </Accordion.Header>
        <Accordion.Body>
          <p>
            This report provides an analysis of total remuneration practices among the organisations that participated in the survey. It presents information on both long-term and short-term incentives, as well as trends in overall remuneration mix across various employee levels, to support pay structuring decisions.
          </p>
          <p>
            The survey included both qualitative and quantitative questions, which were answered based on each company’s grading system. The findings address practices related to:
          </p>
          <ul>
            <li>Long-term incentives (LTI)</li>
            <li>Short-term incentives (STI)</li>
            <li>Total Remuneration Mix</li>
          </ul>
          <p>
            A total of {reportData.metadata.totalQuestionnaires || "—"} organisations participated in the Total Remuneration survey, and this report is based on their REMchannel TGP data submissions as well as their LTI and STI survey responses.
          </p>
          <p>
            A validation process, using the REMchannel methodology, was applied to data on total guaranteed package (TGP). However, information concerning short- and long-term incentives is reported as submitted by participants.
          </p>
          <h6 className="mt-4 mb-2"><b>Caveats and limitations</b></h6>
          <ul>
            <li>The analysis and opinions in this document are based on:</li>
            <ul>
              <li>The facts as presented to the consultants via the data collection process. It must be noted that REMchannel cannot take any responsibility for inaccuracies and the scheme cannot be held responsible for incorrect results as a result of such inaccuracies or data.</li>
              <li>Our knowledge and interpretation of the data as portrayed in discussion with representatives of the participating organisations.</li>
            </ul>
          </ul>

          {hasAllowedRole && (
            <div className="d-flex justify-content-end gap-2 mb-3">
              <Button
                variant="outline-primary"
                onClick={async () => {
                  try {
                    setRetranslating(true);
                    const reportIdToUse = reportId || reportData?.metadata?.reportId || 'unknown';
                    await retranslateReportGrades(reportIdToUse);
                    
                    // Refresh the report data if callback is provided
                    if (onReportRefresh) {
                      await onReportRefresh();
                    } else {
                      // Fallback: show success message and suggest page refresh
                      alert('Report grades retranslated successfully. Please refresh the page to see the updated data.');
                    }
                  } catch (error) {
                    console.error('Error retranslating report grades:', error);
                    alert(`Failed to retranslate report grades: ${error.message}`);
                  } finally {
                    setRetranslating(false);
                  }
                }}
                disabled={retranslating || !reportId}
              >
                {retranslating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Retranslating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Retranslate Grades
                  </>
                )}
              </Button>
              
              <Button
                variant="outline-secondary"
                onClick={async () => {
                  try {
                    setExportingExcel(true);
                    const metadata = reportData.metadata;
                    const reportIdToUse = reportId || metadata?.reportId || 'unknown';
                    const circleIdToUse = circleId || metadata?.circleId || reportData?.circleId;
                    const gradingSystemIdToUse = gradingSystemId || metadata?.gradingSystemId || reportData?.gradingSystemId;
                    const orgIdToUse = organizationId || orgId || metadata?.organizationId;
                    
                    await exportProcessedDataToExcel(
                      reportIdToUse,
                      metadata,
                      circleIdToUse,
                      gradingSystemIdToUse,
                      orgIdToUse,
                      hasAllowedRole, // isKAMUser
                      circleName || circleIdToUse, // circleName - use prop if available, otherwise fallback to ID
                      gradingSystemName || gradingSystemIdToUse // gradingSystemName - will need to be passed from parent, but for now use ID as fallback
                    );
                  } catch (error) {
                    console.error('Error exporting processed data to Excel:', error);
                    alert(`Failed to export processed data: ${error.message}`);
                  } finally {
                    setExportingExcel(false);
                  }
                }}
                disabled={exportingExcel || !reportData?.metadata}
              >
                {exportingExcel ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel me-2"></i>
                    Export to Excel
                  </>
                )}
              </Button>
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="1">
        <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
        LONG-TERM INCENTIVES (LTI) RESULTS
        </Accordion.Header>
        <Accordion.Body>
          {/* Eligibility to Participate
           * This chart displays the eligibility of employees to participate in Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented as percentages showing what portion of employees
           * in each grade are eligible for LTI plans, categorized as 'All', 'Some', or 'None'.
           *
           * The vertical bars represent different job grades, while the colors indicate the eligibility categories.
           * Percentages are displayed on the bars when the value is significant enough to be visible.
           * This visualization helps identify patterns in LTI eligibility across the organization's hierarchy.
           *
           */}
          <h5>LTI eligibility</h5>
          <p>
          Participants reported eligibility for the company’s LTI schemes based on employee grade. They specified whether all, some, or none of the employees in each grade were eligible to participate in the scheme and provided the potential values possible at different performance levels across various percentiles per the table below.
          </p>
          {reportData.chartDataByGradingSystem.ltiEligibility && (
            <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
              {(() => {
                const original =
                  reportData.chartDataByGradingSystem.ltiEligibility;
                const { labels, datasets } = original;

                const gradeMap = {};
                labels.forEach((grade, idx) => {
                  if (!gradeMap[grade]) {
                    gradeMap[grade] = [];
                  }
                  gradeMap[grade].push(idx);
                });

                const uniqueGrades = [];
                labels.forEach((grade) => {
                  if (!uniqueGrades.includes(grade)) {
                    uniqueGrades.push(grade);
                  }
                });

                const combinedDatasets = datasets.map((dataset, dsIdx) => {
                  // For each unique grade, sum the values from all indices where that grade appears
                  const newData = uniqueGrades.map((grade) => {
                    const indices = gradeMap[grade];
                    // Sum the values for this dataset at all indices for this grade
                    return indices.reduce(
                      (sum, idx) => sum + (dataset.data[idx] || 0),
                      0
                    );
                  });
                  return {
                    ...dataset,
                    data: newData,
                    backgroundColor:
                      ltiEligibilityColors[dsIdx % ltiEligibilityColors.length],
                    borderColor:
                      ltiEligibilityColors[dsIdx % ltiEligibilityColors.length],
                    borderWidth: 1,
                  };
                });  

                combinedDatasets.forEach((ds) => {
                  ds.data = ds.data.map((val) => (val > 100 ? 100 : val));
                });

                return (
                  <Bar
                    data={{
                      ...original,
                      labels: uniqueGrades,
                      datasets: combinedDatasets,
                    }}
                    options={{
                      ...reportData.eligibilityChartOptions,
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: "y",
                      scales: {
                        x: {
                          stacked: true,
                          max: 100,
                          title: {
                            display: true,
                            text: "Percentage of Participants",
                          },
                        },
                        y: {
                          title: {
                            display: true,
                            text: "Grades",
                          },
                        },
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: "LTI Eligibility",
                          font: {
                            size: 16,
                          },
                        },
                        ...reportData.eligibilityChartOptions?.plugins,
                        legend: {
                          position: "bottom",
                        },
                        datalabels: {
                          ...reportData.eligibilityChartOptions?.plugins
                            ?.datalabels,
                          formatter: (value) => {
                            return value > 0 ? value + "%" : "";
                          },
                          color: "#fff",
                          font: {
                            weight: "bold",
                          },
                        },
                      },
                    }}
                  />
                );
              })()}
            </div>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {(() => {
            // Find all grades where "None" is NOT 100% in the LTI eligibility chart,
            // but are missing from all LTI datasets due to insufficient data
            const ltiEligibility = reportData.chartDataByGradingSystem.ltiEligibility;
            const ltiThreshold = reportData.chartDataByGradingSystem.ltiThresholdAnnualReward;
            const ltiTarget = reportData.chartDataByGradingSystem.ltiTargetAnnualReward;
            const ltiStretch = reportData.chartDataByGradingSystem.ltiStretchAnnualReward;

            if (
              ltiEligibility &&
              Array.isArray(ltiEligibility.labels) &&
              Array.isArray(ltiEligibility.datasets)
            ) {
              // Find the dataset index for "None"
              const noneIdx = ltiEligibility.datasets.findIndex(
                ds => ds.label && ds.label.toLowerCase().includes("none")
              );
              let gradesWithNoneNot100 = [];
              if (noneIdx !== -1) {
                gradesWithNoneNot100 = ltiEligibility.labels.filter((grade, i) => {
                  const val = ltiEligibility.datasets[noneIdx].data[i];
                  return Number(val) !== 100;
                });
              }

              // Collect all grades present in any LTI dataset
              const ltiGrades = new Set([
                ...(ltiThreshold?.labels || []),
                ...(ltiTarget?.labels || []),
                ...(ltiStretch?.labels || []),
              ]);

              // Filter gradesWithNoneNot100 to those NOT present in any LTI dataset
              const missingGrades = gradesWithNoneNot100.filter(
                grade => !ltiGrades.has(grade)
              );

              if (missingGrades.length > 0 && (!hiddenSections.ltiParticipationBaseSalary || !hiddenSections.ltiParticipationTGP)) {
                return (
                  <div className="alert alert-warning my-2" style={{ fontSize: "0.95em" }}>
                    <strong>Note:</strong> The following grades have LTI eligibility but are not shown in the LTI tables below due to insufficient LTI data:{" "}
                    <span style={{ fontWeight: 700 }}>
                      {missingGrades.join(", ")}
                    </span>
                  </div>
                );
              }
            }
            return null;
          })()}

          {(() => {
            // Defensive: check data exists
            const threshold =
              reportData.chartDataByGradingSystem.ltiThresholdAnnualReward;
            const target =
              reportData.chartDataByGradingSystem.ltiTargetAnnualReward;
            const stretch =
              reportData.chartDataByGradingSystem.ltiStretchAnnualReward;

            // Get all grades from the labels array (shared by all datasets)
            const grades = threshold?.labels || [];

            // Table rows for percentiles and their corresponding dataset labels
            const percentiles = [
              {
                label: (
                  <>
                    25<sup>th</sup>
                  </>
                ),
                base: "Base Salary P25",
                tgp: "TGP P25",
              },
              {
                label: (
                  <>
                    50<sup>th</sup>
                  </>
                ),
                base: "Base Salary P50",
                tgp: "TGP P50",
              },
              {
                label: (
                  <>
                    75<sup>th</sup>
                  </>
                ),
                base: "Base Salary P75",
                tgp: "TGP P75",
              },
              {
                label: "Average",
                base: "Base Salary Average",
                tgp: "TGP Average",
              },
            ];

            // Helper to get value from dataset by label and grade index
            function getValue(ds, label, gradeIndex) {
              const dataset = ds?.datasets?.find((d) => d.label === label);
              if (!dataset || gradeIndex === -1) return null;
              return dataset.data[gradeIndex];
            }

            // Helper to format as currency
            function formatR(val) {
              if (val == null || isNaN(val)) return "";
              return (
                "R " +
                Number(val).toLocaleString("en-ZA", {
                  maximumFractionDigits: 0,
                })
              );
            }

            // Helper to build grade indices map
            function buildGradeIndicesMap(grades) {
              return grades.reduce((acc, grade, idx) => {
                if (!acc[grade]) acc[grade] = [];
                acc[grade].push(idx);
                return acc;
              }, {});
            }

            // Helper to calculate average value
            function avgValue(ds, label, indices) {
              const values = indices
                .map((gIdx) => getValue(ds, label, gIdx))
                .filter((v) => v != null && !isNaN(v));
              if (!values.length) return null;
              return (
                values.reduce((sum, v) => sum + Number(v), 0) /
                values.length
              );
            }

            const gradeIndicesMap = buildGradeIndicesMap(grades);
            const uniqueGradeNames = Object.keys(gradeIndicesMap);

            return (
              <>
                {!hiddenSections.ltiParticipationBaseSalary && (
                  <>
                    <h5>LTI participation based on Base salary</h5>
                    {/* Base Salary Table */}
                    <Table striped bordered hover responsive className="mt-3">
                      <thead>
                        <tr>
                          <th style={{ width: "120px" }}>Grade</th>
                          <th>Percentile</th>
                          <th>Threshold LTI values (Base salary)</th>
                          <th>Target LTI values (Base salary)</th>
                          <th>Stretch LTI values (Base salary)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueGradeNames.map((grade) =>
                          percentiles.map((p, pIdx) => {
                            const indices = gradeIndicesMap[grade];
                            return (
                              <tr key={grade + "-base-" + pIdx}>
                                {pIdx === 0 && (
                                  <td rowSpan={percentiles.length}>{grade}</td>
                                )}
                                <td>{p.label}</td>
                                <td>{formatR(avgValue(threshold, p.base, indices))}</td>
                                <td>{formatR(avgValue(target, p.base, indices))}</td>
                                <td>{formatR(avgValue(stretch, p.base, indices))}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </>
                )}

                {!hiddenSections.ltiParticipationTGP && (
                  <>
                    <h6 className="mt-4">
                      LTI participation based on Total guaranteed package (TGP)
                    </h6>
                    {/* TGP Table */}
                    <Table striped bordered hover responsive className="mt-3">
                      <thead>
                        <tr>
                          <th style={{ width: "120px" }}>Grade</th>
                          <th>Percentile</th>
                          <th>Threshold LTI values (TGP)</th>
                          <th>Target LTI values (TGP)</th>
                          <th>Stretch LTI values (TGP)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueGradeNames.map((grade) =>
                          percentiles.map((p, pIdx) => {
                            const indices = gradeIndicesMap[grade];
                            return (
                              <tr key={grade + "-tgp-" + pIdx}>
                                {pIdx === 0 && (
                                  <td rowSpan={percentiles.length}>{grade}</td>
                                )}
                                <td>{p.label}</td>
                                <td>{formatR(avgValue(threshold, p.tgp, indices))}</td>
                                <td>{formatR(avgValue(target, p.tgp, indices))}</td>
                                <td>{formatR(avgValue(stretch, p.tgp, indices))}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            );
          })()}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {!hiddenSections.ltiAnnualAwards && (
            <>
          {/* LTI Annual Awards
           * This section provides detailed information on the annual awards for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a table showing the distribution of the annual awards.
           *
           */}
          <h5>LTI annual awards by remuneration base</h5>
          <p>
            The table below shows the distribution of the annual awards for LTI.
            The base type is the basis on which the annual awards are
            calculated.
          </p>
          <Table striped bordered hover responsive className="mt-3">
            <thead>
              <tr>
                <th>LTI Remuneration Base</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Try to get baseTypeDistribution from reportData, fallback to empty object if not present
                const ltiBaseTypeDistribution =
                  reportData?.chartDataByGradingSystem?.baseTypeDistribution
                    ?.lti || {};

                // Map for display names
                const baseTypeLabels = {
                  percentage_base: "Percentage of Base Pay",
                  percentage_tgp: "Percentage of Total Guaranteed Package",
                  monetary_value: "Monetary Value by Grade",
                  other: "Other",
                };

                // If no data, show "No data available"
                const keys = Object.keys(ltiBaseTypeDistribution);
                if (!keys.length) {
                  return (
                    <tr>
                      <td colSpan="2" className="text-center">
                        No data available
                      </td>
                    </tr>
                  );
                }

                // Show all base types (even if 0%)
                // Sort keys alphabetically by base type label (or key if label not found)
                return keys
                  .sort((a, b) => {
                    const labelA = baseTypeLabels[a] || a;
                    const labelB = baseTypeLabels[b] || b;
                    return labelA.localeCompare(labelB);
                  })
                  .map((key) => (
                    <tr key={key}>
                      <td>{baseTypeLabels[key] || key}</td>
                      <td>{Math.ceil(ltiBaseTypeDistribution[key])}%</td>
                    </tr>
                  ));
              })()}
            </tbody>
          </Table>
            </>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {!hiddenSections.ltiAllocationPercentagesByGrade && (
            <>
          <h5>LTI allocation percentages by grade</h5>
          <p>
            The table below shows the average allocation percentages per grade for LTI. Participants who entered monetary values were considered as a percentage of TGP basis to be included in these percentages. All values are rounded up to the nearest whole percentage.
          </p>
          {(() => {
            const ltiBaseTypeAvg =
              reportData?.chartDataByGradingSystem?.baseTypeAverageOfPercentages?.lti;

            if (
              !ltiBaseTypeAvg ||
              !Array.isArray(ltiBaseTypeAvg.labels) ||
              !Array.isArray(ltiBaseTypeAvg.datasets) ||
              ltiBaseTypeAvg.datasets.length === 0
            ) {
              return (
                <Table striped bordered hover responsive className="mt-3 mb-4">
                  <thead>
                    <tr>
                      <th>Grade</th>
                      <th>Threshold</th>
                      <th>Target</th>
                      <th>Stretch</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="4" className="text-center">
                        No data available
                      </td>
                    </tr>
                  </tbody>
                </Table>
              );
            }

            // Map dataset label to its data array for easy lookup
            const datasetMap = {};
            ltiBaseTypeAvg.datasets.forEach((ds) => {
              datasetMap[ds.label] = ds.data;
            });

            // Get all unique dataset labels in the order: Threshold, Target, Stretch (if present)
            const allLabels = ltiBaseTypeAvg.datasets.map((ds) => ds.label);
            const orderedLabels = ["Threshold", "Target", "Stretch"].filter((lbl) =>
              allLabels.includes(lbl)
            );

            return (
              <Table striped bordered hover responsive className="mt-3 mb-4">
                <thead>
                  <tr>
                    <th>Grade</th>
                    {orderedLabels.map((lbl) => (
                      <th key={lbl}>{lbl}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ltiBaseTypeAvg.labels.map((grade, idx) => (
                    <tr key={grade}>
                      <td>{grade}</td>
                      {orderedLabels.map((lbl) => {
                        const value = datasetMap[lbl]?.[idx];
                        // Show as integer, rounded up, or "-" if not present
                        return (
                          <td key={lbl}>
                            {typeof value === "number" && !isNaN(value)
                              ? Math.ceil(value) + "%"
                              : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            );
          })()}
            </>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* LTI Plans
           * This section provides detailed information on the long-term incentive plans as a percentage of remuneration
           * for different job grades. The data is presented in a chart showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5 className="mt-4">LTI plan types</h5>
          <p className="text-muted">
          The graph below indicates the types of LTI plans applicable at  each eligible grade.
          </p>
          {reportData.chartDataByGradingSystem?.ltiPlans && (() => {
            const ltiPlans = reportData.chartDataByGradingSystem.ltiPlans;

            // Dynamically build a mapping from label to display label
            // Try to use a mapping if available, otherwise format label
            const planTypeLabels = {
              stock_options: "Stock/Share Options",
              sars: "Share Appreciation Rights (SARs)",
              fsp: "Forfeitable Share Plan (FSP)",
              conditional_share: "Conditional Share Plan",
              single_incentive: "Single Incentive Plan",
              value_appreciation: "Value Appreciation Rights",
              deferred_bonus: "Deferred Bonus Plan",
              other: "Other"
            };

            // Get all unique plan type keys in the order they appear in the data
            const planTypeOrder = ltiPlans.datasets.map(ds => ds.label);

            // Build datasets with dynamic color assignment and display label
            const datasets = ltiPlans.datasets.map((ds, idx) => ({
              ...ds,
              label: planTypeLabels[ds.label] || formatLegendLabel(ds.label),
              backgroundColor: ltiPlansColors[idx % ltiPlansColors.length],
              borderColor: ltiPlansColors[idx % ltiPlansColors.length],
              borderWidth: 1,
            }));

            return (
              <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
                <Bar
                  data={{
                    ...ltiPlans,
                    datasets,
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        stacked: true,
                        title: {
                          display: true,
                          text: "Grade",
                        },
                      },
                      y: {
                        stacked: true,
                        title: {
                          display: true,
                          text: "Percentage (%)",
                        },
                        max: 100,
                      },
                    },
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          boxWidth: 12,
                          generateLabels: (chart) => {
                            const datasets = chart.data.datasets;
                            return datasets.map((dataset, i) => ({
                              text: formatLegendLabel(dataset.label),
                              fillStyle: dataset.backgroundColor,
                              strokeStyle: dataset.borderColor,
                              lineWidth: dataset.borderWidth,
                              hidden: !chart.isDatasetVisible(i),
                              index: i,
                            }));
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `${formatLegendLabel(context.dataset.label)}: ${context.raw}%`;
                          },
                        },
                      },
                      datalabels: {
                        formatter: (value) => {
                          return value > 0 ? value + "%" : "";
                        },
                        color: "#fff",
                        font: {
                          weight: "bold",
                        },
                      },
                    },
                  }}
                />
              </div>
            );
          })()}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* Application of Performance Conditions
           * This section provides detailed information on the application of performance conditions for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a chart showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5>LTI performance conditions applicable</h5>
          <p>
          The graph below displays the performance conditions typically linked to LTI plans and the percentage of participants who reported using them.
          </p>
          {reportData.chartDataByGradingSystem?.ltiPlansPerformanceConditions &&
            (() => {
              // Dynamically calculate the total for each category across all types
              const ltiData =
                reportData.chartDataByGradingSystem
                  .ltiPlansPerformanceConditions;
              // Map label to color for legend and chart
              const labelColorMap = {
                "Performance Conditions Apply": "#003366",
                "No Performance Conditions": "#FF6B00",
              };

              // The order of these keys determines the order in the chart and legend
              const labelOrder = [
                "Performance Conditions Apply",
                "No Performance Conditions",
              ];

              // Sum up the data for each label across all types
              const categoryTotals = labelOrder.map((label) => {
                const dataset = ltiData.datasets.find(
                  (ds) => ds.label === label
                );
                if (!dataset) return 0;
                // Sum all values in the data array for this dataset
                return dataset.data.reduce(
                  (sum, val) => sum + (typeof val === "number" ? val : 0),
                  0
                );
              });

              // Calculate the total sum for percentage calculation
              const total = categoryTotals.reduce((sum, val) => sum + val, 0);

              // Calculate percentages, rounded to nearest integer
              const percentages = categoryTotals.map((val) =>
                total > 0 ? Math.round((val / total) * 100) : 0
              );

              return (
                <div
                  style={{
                    height: "400px",
                    marginBottom: "2rem",
                    maxWidth: "600px",
                    margin: "0 auto",
                  }}
                >
                  <Doughnut
                    data={{
                      labels: [
                        "Performance conditions",
                        "No performance conditions",
                      ],
                      datasets: [
                        {
                          data: percentages,
                          backgroundColor: labelOrder.map(
                            (label) => labelColorMap[label]
                          ),
                          borderColor: labelOrder.map(
                            (label) => labelColorMap[label]
                          ),
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "65%",
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            boxWidth: 12,
                            padding: 20,
                            color: "#333",
                            font: {
                              size: 13,
                            },
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const label = context.label || "";
                              const value = context.raw || 0;
                              return `${label}: ${value}%`;
                            },
                          },
                        },
                        datalabels: {
                          display: function (context) {
                            return context.dataset.data[context.dataIndex] > 0;
                          },
                          formatter: function (value) {
                            return value + "%";
                          },
                          color: "#fff",
                          font: {
                            size: 9,
                            weight: "bold",
                          },
                          anchor: "center",
                          align: "center",
                        },
                      },
                    }}
                  />
                </div>
              );
            })()}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* LTI Performance Conditions
           * This section provides detailed information on the performance conditions used for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a chart showing the percentage of companies that use various performance conditions for LTI allocations.
           *
           */}
          <h5>LTI performance conditions</h5>
          <p>
          The following information outlines the percentage of LTI plans that include performance conditions compared to those without applicable performance conditions.  
          </p>
          {reportData.chartDataByGradingSystem.ltiPlanDistribution && (
            <div style={{ height: "500px", marginBottom: "2rem" }}>
              <Bar
                data={{
                  ...reportData.chartDataByGradingSystem.ltiPlanDistribution,
                  datasets:
                    reportData.chartDataByGradingSystem.ltiPlanDistribution.datasets.map(
                      (dataset, index) => ({
                        ...dataset,
                        backgroundColor: dataset.data.map(
                          (_, i) =>
                            stiPerformanceAssessmentColors[
                              i % stiPerformanceAssessmentColors.length
                            ]
                        ),
                        borderColor: dataset.data.map(
                          (_, i) =>
                            stiPerformanceAssessmentColors[
                              i % stiPerformanceAssessmentColors.length
                            ]
                        ),
                        borderWidth: 1,
                      })
                    ),
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "x",
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return `${context.raw}%`;
                        },
                      },
                    },
                    datalabels: {
                      display: function (context) {
                        return context.dataset.data[context.dataIndex] > 0;
                      },
                      formatter: function (value) {
                        return value + "%";
                      },
                      color: "#fff",
                      font: {
                        size: 9,
                        weight: "bold",
                      },
                      anchor: "center",
                      align: "center",
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      ticks: {
                        callback: function (value) {
                          return value + "%";
                        },
                      },
                    },
                    x: {
                      ticks: {
                        font: {
                          size: 11,
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          )}
        

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* Vesting Methodology
           * This section provides detailed information on the vesting methodology used for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a chart showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5>LTI vesting methodology</h5>
          <p className="text-muted mb-3">
          The following graph illustrates the various vesting methods employed, along with the corresponding percentage of participants utilising each approach.
          </p>
          {reportData.chartDataByGradingSystem &&
            reportData.chartDataByGradingSystem.ltiVestingMethodology && (
              <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
                <Bar
                  data={{
                    ...reportData.chartDataByGradingSystem
                      .ltiVestingMethodology,
                    datasets:
                      reportData.chartDataByGradingSystem.ltiVestingMethodology.datasets.map(
                        (dataset, index) => ({
                          ...dataset,
                          backgroundColor:
                            ltiVestingMethodologyColors[
                              index % ltiVestingMethodologyColors.length
                            ],
                          borderColor:
                            ltiVestingMethodologyColors[
                              index % ltiVestingMethodologyColors.length
                            ],
                          borderWidth: 1,
                        })
                      ),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        stacked: true,
                        title: {
                          display: true,
                          text: "Grade",
                        },
                      },
                      y: {
                        stacked: true,
                        title: {
                          display: true,
                          text: "Percentage (%)",
                        },
                        max: 100,
                      },
                    },
                    plugins: {
                      title: {
                        display: true,
                        text: "Vesting Mechanism by Grade",
                        font: {
                          size: 16,
                        },
                      },
                      legend: {
                        position: "bottom",
                        labels: {
                          generateLabels: (chart) => {
                            const datasets = chart.data.datasets;
                            return datasets.map((dataset, i) => {
                              const label = dataset.label || `Dataset ${i + 1}`;
                              const color =
                                ltiVestingMethodologyColors[
                                  i % ltiVestingMethodologyColors.length
                                ];

                              return {
                                text: formatLegendLabel(label),
                                fillStyle: color,
                                strokeStyle: color,
                                lineWidth: 1,
                                hidden: !chart.isDatasetVisible(i),
                                index: i,
                                datasetIndex: i,
                              };
                            });
                          },
                        },
                      },
                      datalabels: {
                        formatter: (value) => {
                          return value > 0 ? value + "%" : "";
                        },
                        color: "#fff",
                        font: {
                          weight: "bold",
                          size: 9,
                        },
                      },
                    },
                  }}
                />
              </div>
            )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* Vesting Periods by Long-term Incentive Type
           * This section provides detailed information on the vesting periods by long-term incentive type for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a table showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5>LTI vesting periods</h5>
          <p>
          Participants reported the vesting period by grade as illustrated in the table below.  
          </p>

          <div className="table-responsive mb-4">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th style={{ width: "120px", backgroundColor: "#4CAF50", color: "white" }}>
                    Vesting
                  </th>
                  {reportData.chartDataByGradingSystem &&
                    reportData.chartDataByGradingSystem.ltiVestingPeriods &&
                    reportData.chartDataByGradingSystem.ltiVestingPeriods.labels.map(
                      (label) => (
                        <th
                          key={label}
                          style={{ backgroundColor: "#4CAF50", color: "white" }}
                        >
                          {label}
                        </th>
                      )
                    )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Use the same ordering as the chart below
                  const ltiVestingPeriods = reportData.chartDataByGradingSystem &&
                    reportData.chartDataByGradingSystem.ltiVestingPeriods;

                  if (!ltiVestingPeriods) return null;

                  const desiredOrder = [
                    "Annually",
                    "Two Years",
                    "Three Years",
                    "Four Years",
                    "Five Years",
                    "Longer Than Five Years",
                  ];

                  const normalize = (label) =>
                    label
                      .replace(/_/g, " ")
                      .replace(/\s+/g, " ")
                      .trim()
                      .toLowerCase();

                  // Build a map from normalized label to dataset
                  const datasetMap = {};
                  ltiVestingPeriods.datasets.forEach(
                    (dataset) => {
                      datasetMap[normalize(dataset.label)] = dataset;
                    }
                  );

                  // Build the reordered datasets array according to desiredOrder
                  const reorderedDatasets = desiredOrder
                    .map((orderLabel) => datasetMap[normalize(orderLabel)])
                    .filter(Boolean);

                  // If not all desired rows are present, append any missing ones from the original
                  const usedLabels = new Set(reorderedDatasets.map(ds => ds.label));
                  ltiVestingPeriods.datasets.forEach(ds => {
                    if (!usedLabels.has(ds.label)) {
                      reorderedDatasets.push(ds);
                    }
                  });

                  return reorderedDatasets.map((dataset) => (
                    <tr key={`vesting-${dataset.label}`}>
                      <td
                        style={{ backgroundColor: "#4CAF50", color: "white" }}
                      >
                        {formatLegendLabel(dataset.label)}
                      </td>
                      {dataset.data.map((value, index) => (
                        <td key={index}>{value}%</td>
                      ))}
                    </tr>
                  ));
                })()}
              </tbody>
            </Table>
          </div>

          {reportData.chartDataByGradingSystem &&
            reportData.chartDataByGradingSystem.ltiVestingPeriods && (() => {
              // Define the desired order for the rows (case-insensitive, ignore underscores)
              const desiredOrder = [
                "Annually",
                "Two Years",
                "Three Years",
                "Four Years",
                "Five Years",
                "Longer Than Five Years",
              ];

              // Helper to normalize label for matching
              const normalize = (label) =>
                label
                  .replace(/_/g, " ")
                  .replace(/\s+/g, " ")
                  .trim()
                  .toLowerCase();

              // Build a map from normalized label to dataset
              const datasetMap = {};
              reportData.chartDataByGradingSystem.ltiVestingPeriods.datasets.forEach(
                (dataset) => {
                  datasetMap[normalize(dataset.label)] = dataset;
                }
              );

              // Build the reordered datasets array according to desiredOrder
              const reorderedDatasets = desiredOrder
                .map((orderLabel) => datasetMap[normalize(orderLabel)])
                .filter(Boolean);

              // If not all desired rows are present, append any missing ones from the original
              const usedLabels = new Set(reorderedDatasets.map(ds => ds.label));
              reportData.chartDataByGradingSystem.ltiVestingPeriods.datasets.forEach(ds => {
                if (!usedLabels.has(ds.label)) {
                  reorderedDatasets.push(ds);
                }
              });

              return (
                <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
                  <Bar
                    data={{
                      ...reportData.chartDataByGradingSystem.ltiVestingPeriods,
                      datasets: reorderedDatasets.map((dataset, index) => ({
                        ...dataset,
                        backgroundColor:
                          ltiVestingPeriodsColors[
                            index % ltiVestingPeriodsColors.length
                          ],
                        borderColor:
                          ltiVestingPeriodsColors[
                            index % ltiVestingPeriodsColors.length
                          ],
                        borderWidth: 1,
                      })),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: "Grade",
                          },
                        },
                        y: {
                          title: {
                            display: true,
                            text: "Percentage (%)",
                          },
                          max: 100,
                        },
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: "Vesting Periods by Grade",
                        },
                        legend: {
                          position: "bottom",
                          labels: {
                            generateLabels: (chart) => {
                              const datasets = chart.data.datasets;
                              return datasets.map((dataset, i) => {
                                const label = dataset.label || `Dataset ${i + 1}`;
                                const color =
                                  ltiVestingPeriodsColors[
                                    i % ltiVestingPeriodsColors.length
                                  ];

                                return {
                                  text: formatLegendLabel(label),
                                  fillStyle: color,
                                  strokeStyle: color,
                                  lineWidth: 1,
                                  hidden: !chart.isDatasetVisible(i),
                                  index: i,
                                };
                              });
                            },
                          },
                        },
                        datalabels: {
                          formatter: (value) => {
                            return value > 0 ? value + "%" : "";
                          },
                          color: "#fff",
                          font: {
                            weight: "bold",
                            size: 9,
                          },
                        },
                      },
                    }}
                  />
                </div>
              );
            })()}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* Settlement Methodology
           * This section provides detailed information on the settlement methodology used for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a chart showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5>LTI settlement methodology</h5>
          <p>
          The graph below shows how vested awards are settled.
          </p>
          {reportData.chartDataByGradingSystem &&
            reportData.chartDataByGradingSystem.ltiSettlementMethodology && (
              <div
                style={{
                  height: "400px",
                  marginBottom: "2rem",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                <Doughnut
                  data={{
                    ...reportData.chartDataByGradingSystem
                      .ltiSettlementMethodology,
                    datasets:
                      reportData.chartDataByGradingSystem.ltiSettlementMethodology.datasets.map(
                        (dataset) => ({
                          ...dataset,
                          backgroundColor: dataset.data.map(
                            (_, i) =>
                              ltiSettlementMethodologyColors[
                                i % ltiSettlementMethodologyColors.length
                              ]
                          ),
                          borderColor: dataset.data.map(
                            (_, i) =>
                              ltiSettlementMethodologyColors[
                                i % ltiSettlementMethodologyColors.length
                              ]
                          ),
                          borderWidth: 1,
                        })
                      ),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "60%",
                    plugins: {
                      title: {
                        display: true,
                        text: "LTI Settlement Methodology",
                        font: {
                          size: 16,
                        },
                      },
                      legend: {
                        position: "bottom",
                        labels: {
                          generateLabels: (chart) => {
                            const datasets = chart.data.datasets;
                            return (datasets[0]?.data ?? []).map((_, i) => {
                              const label = chart.data.labels?.[i] || `Segment ${i + 1}`;
                              const color = datasets[0].backgroundColor?.[i] ?? datasets[0].borderColor?.[i];
                              return {
                                text: formatLegendLabel(String(label)),
                                fillStyle: color,
                                strokeStyle: color,
                                lineWidth: 1,
                                hidden: false,
                                index: i,
                              };
                            });
                          },
                        },
                      },
                      datalabels: {
                        formatter: (value) => {
                          return value > 0 ? value + "%" : "";
                        },
                        color: "#fff",
                        font: {
                          weight: "bold",
                        },
                      },
                    },
                  }}
                />
              </div>
            )}
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="2">
        <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
        SHORT-TERM INCENTIVES ( STI) RESULTS
        </Accordion.Header>
        <Accordion.Body>
          <h5>STI eligibility</h5>
          <p>
          Participants reported eligibility for the company’s STI schemes based on employee grade. They specified whether all, some, or none of the employees in each grade were eligible to participate in the scheme and provided the potential values possible at different performance levels across various percentiles per the table below.  
          </p>
          {reportData.chartDataByGradingSystem &&
            reportData.chartDataByGradingSystem.stiEligibility && (
              <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
                <Bar
                  data={{
                    ...reportData.chartDataByGradingSystem.stiEligibility,
                    datasets:
                      reportData.chartDataByGradingSystem.stiEligibility.datasets.map(
                        (dataset, index) => ({
                          ...dataset,
                          backgroundColor:
                            stiEligibilityColors[
                              index % stiEligibilityColors.length
                            ],
                          borderColor:
                            stiEligibilityColors[
                              index % stiEligibilityColors.length
                            ],
                          borderWidth: 1,
                        })
                      ),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: "y",
                    scales: {
                      x: {
                        stacked: true,
                        max: 100,
                        title: {
                          display: true,
                          text: "Percentage of Participants",
                        },
                      },
                      y: {
                        stacked: true,
                        title: {
                          display: true,
                          text: "Grades",
                        },
                      },
                    },
                    plugins: {
                      title: {
                        display: true,
                        text: "Eligibility to participate in Short-term Incentive Schemes",
                        font: {
                          size: 16,
                        },
                      },
                      legend: {
                        position: "bottom",
                        labels: {
                          generateLabels: (chart) => {
                            const datasets = chart.data.datasets;
                            return datasets.map((dataset, i) => ({
                              text: formatLegendLabel(dataset.label ?? ""),
                              fillStyle: dataset.backgroundColor,
                              strokeStyle: dataset.borderColor,
                              lineWidth: dataset.borderWidth ?? 1,
                              hidden: !chart.isDatasetVisible(i),
                              index: i,
                            }));
                          },
                        },
                      },
                      datalabels: {
                        formatter: (value) => {
                          return value > 0 ? value + "%" : "";
                        },
                        color: "#fff",
                        font: {
                          weight: "bold",
                        },
                      },
                    },
                  }}
                />
              </div>
            )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />
 
          {(() => {
            // Find all grades where "None" is NOT 100% in the STI eligibility chart,
            // but are missing from all STI datasets due to insufficient data
            const stiEligibility = reportData.chartDataByGradingSystem.stiEligibility;
            const stiThreshold = reportData.chartDataByGradingSystem.stiThresholdAnnualReward;
            const stiTarget = reportData.chartDataByGradingSystem.stiTargetAnnualReward;
            const stiStretch = reportData.chartDataByGradingSystem.stiStretchAnnualReward;

            if (
              stiEligibility &&
              Array.isArray(stiEligibility.labels) &&
              Array.isArray(stiEligibility.datasets)
            ) {
              // Find the dataset index for "None"
              const noneIdx = stiEligibility.datasets.findIndex(
                ds => ds.label && ds.label.toLowerCase().includes("none")
              );
              let gradesWithNoneNot100 = [];
              if (noneIdx !== -1) {
                gradesWithNoneNot100 = stiEligibility.labels.filter((grade, i) => {
                  const val = stiEligibility.datasets[noneIdx].data[i];
                  return Number(val) !== 100;
                });
              }

              // Collect all grades present in any STI dataset
              const stiGrades = new Set([
                ...(stiThreshold?.labels || []),
                ...(stiTarget?.labels || []),
                ...(stiStretch?.labels || []),
              ]);

              // Filter gradesWithNoneNot100 to those NOT present in any STI dataset
              const missingGrades = gradesWithNoneNot100.filter(
                grade => !stiGrades.has(grade)
              );

              if (missingGrades.length > 0 && (!hiddenSections.stiParticipationBaseSalary || !hiddenSections.stiParticipationTGP)) {
                return (
                  <div className="alert alert-warning my-2" style={{ fontSize: "0.95em" }}>
                    <strong>Note:</strong> The following grades have STI eligibility but are not shown in the STI tables below due to insufficient STI data:{" "}
                    <span style={{ fontWeight: 700 }}>
                      {missingGrades.join(", ")}
                    </span>
                  </div>
                );
              }
            }
            return null;
          })()}

          {(() => {
            // Defensive: check data exists
            if (!reportData?.chartDataByGradingSystem) {
              return null;
            }
            
            const threshold =
              reportData.chartDataByGradingSystem.stiThresholdAnnualReward;
            const target =
              reportData.chartDataByGradingSystem.stiTargetAnnualReward;
            const stretch =
              reportData.chartDataByGradingSystem.stiStretchAnnualReward;

            // Get all grades from the labels array (shared by all datasets)
            const grades = threshold?.labels || [];

            // Table rows for percentiles and their corresponding dataset labels
            const percentiles = [
              {
                label: (
                  <>
                    25<sup>th</sup>
                  </>
                ),
                base: "Base Salary P25",
                tgp: "TGP P25",
              },
              {
                label: (
                  <>
                    50<sup>th</sup>
                  </>
                ),
                base: "Base Salary P50",
                tgp: "TGP P50",
              },
              {
                label: (
                  <>
                    75<sup>th</sup>
                  </>
                ),
                base: "Base Salary P75",
                tgp: "TGP P75",
              },
              {
                label: "Average",
                base: "Base Salary Average",
                tgp: "TGP Average",
              },
            ];

            // Helper to get value from dataset by label and grade index
            function getValue(ds, label, gradeIndex) {
              const dataset = ds?.datasets?.find((d) => d.label === label);
              if (!dataset || gradeIndex === -1) return null;
              return dataset.data[gradeIndex];
            }

            // Helper to format as currency
            function formatR(val) {
              if (val == null || isNaN(val)) return "";
              return (
                "R " +
                Number(val).toLocaleString("en-ZA", {
                  maximumFractionDigits: 0,
                })
              );
            }

            // Helper to build grade indices map
            function buildGradeIndicesMap(grades) {
              return grades.reduce((acc, grade, idx) => {
                if (!acc[grade]) acc[grade] = [];
                acc[grade].push(idx);
                return acc;
              }, {});
            }

            // Helper to calculate average value
            function avgValue(ds, label, indices) {
              const values = indices
                .map((gIdx) => getValue(ds, label, gIdx))
                .filter((v) => v != null && !isNaN(v));
              if (!values.length) return null;
              return (
                values.reduce((sum, v) => sum + Number(v), 0) /
                values.length
              );
            }

            const gradeIndicesMap = buildGradeIndicesMap(grades);
            const uniqueGradeNames = Object.keys(gradeIndicesMap);

            return (
              <>
                {!hiddenSections.stiParticipationBaseSalary && (
                  <>
                    <h5>STI participation based on Base salary </h5>
                    {/* Base Salary Table */}
                    <Table striped bordered hover responsive className="mt-3">
                      <thead>
                        <tr>
                          <th style={{ width: "120px" }}>Grade</th>
                          <th>Percentile</th>
                          <th>Threshold STI values (Base salary)</th>
                          <th>Target STI values (Base salary)</th>
                          <th>Stretch Target STI values (Base salary)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueGradeNames.map((grade) =>
                          percentiles.map((p, pIdx) => {
                            const indices = gradeIndicesMap[grade];
                            return (
                              <tr key={grade + "-base-" + pIdx}>
                                {pIdx === 0 && (
                                  <td rowSpan={percentiles.length}>{grade}</td>
                                )}
                                <td>{p.label}</td>
                                <td>{formatR(avgValue(threshold, p.base, indices))}</td>
                                <td>{formatR(avgValue(target, p.base, indices))}</td>
                                <td>{formatR(avgValue(stretch, p.base, indices))}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </>
                )}

                {!hiddenSections.stiParticipationTGP && (
                  <>
                    <h5>STI participation based on Total guaranteed package (TGP)</h5>
                    {/* TGP Table */}
                    <Table striped bordered hover responsive className="mt-3">
                      <thead>
                        <tr>
                          <th style={{ width: "120px" }}>Grade</th>
                          <th>Percentile</th>
                          <th>Threshold STI values (TGP)</th>
                          <th>Target STI values (TGP)</th>
                          <th>Stretch STI values (TGP)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueGradeNames.map((grade) =>
                          percentiles.map((p, pIdx) => {
                            const indices = gradeIndicesMap[grade];
                            return (
                              <tr key={grade + "-tgp-" + pIdx}>
                                {pIdx === 0 && (
                                  <td rowSpan={percentiles.length}>{grade}</td>
                                )}
                                <td>{p.label}</td>
                                <td>{formatR(avgValue(threshold, p.tgp, indices))}</td>
                                <td>{formatR(avgValue(target, p.tgp, indices))}</td>
                                <td>{formatR(avgValue(stretch, p.tgp, indices))}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            );
          })()}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {!hiddenSections.stiAllocationPercentagesByRemunerationBase && (
            <>
          {/* STI Annual Awards
           * This section provides detailed information on the annual awards for Long-Term Incentive (LTI) plans
           * across different job grades. The data is presented in a table showing the distribution of the annual awards.
           *
           */}
          <h5>STI allocation percentages by remuneration base</h5>
          <p>
          The table below shows the average allocation percentages per grade for STI. Participants who entered monetary values were considered as a percentage of TGP basis to be included in these percentages. All values are rounded up to the nearest whole percentage.
          </p>
          <Table striped bordered hover responsive className="mt-3">
            <thead>
              <tr>
                <th>STI Remuneration Base</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Try to get baseTypeDistribution from reportData, fallback to empty object if not present
                const stiBaseTypeDistribution =
                  reportData?.chartDataByGradingSystem?.baseTypeDistribution
                    ?.sti || {};

                // Map for display names
                const baseTypeLabels = {
                  percentage_base: "Percentage of Base Pay",
                  percentage_tgp: "Percentage of Total Guaranteed Package",
                  monetary_value: "Monetary Value by Grade",
                  other: "Other",
                };

                // If no data, show "No data available"
                const keys = Object.keys(stiBaseTypeDistribution);
                if (!keys.length) {
                  return (
                    <tr>
                      <td colSpan="2" className="text-center">
                        No data available
                      </td>
                    </tr>
                  );
                }

                // Show all base types (even if 0%)
                // Sort keys alphabetically by base type label (or key if label not found)
                return keys
                  .sort((a, b) => {
                    const labelA = baseTypeLabels[a] || a;
                    const labelB = baseTypeLabels[b] || b;
                    return labelA.localeCompare(labelB);
                  })
                  .map((key) => (
                    <tr key={key}>
                      <td>{baseTypeLabels[key] || key}</td>
                      <td>{Math.ceil(stiBaseTypeDistribution[key])}%</td>
                    </tr>
                  ));
              })()}
            </tbody>
          </Table>
            </>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {!hiddenSections.stiAllocationPercentagesByGrade && (
            <>
          <h5>STI allocation percentages by grade</h5>
          <p>
            The table below shows the average allocation percentages per grade for STI. Participants who entered monetary values were considered as a percentage of TGP basis to be included in these percentages. All values are rounded up to the nearest whole percentage.
          </p>
          {(() => {
            const stiBaseTypeAvg =
              reportData?.chartDataByGradingSystem?.baseTypeAverageOfPercentages?.sti;

            if (
              !stiBaseTypeAvg ||
              !Array.isArray(stiBaseTypeAvg.labels) ||
              !Array.isArray(stiBaseTypeAvg.datasets) ||
              stiBaseTypeAvg.datasets.length === 0
            ) {
              return (
                <Table striped bordered hover responsive className="mt-3 mb-4">
                  <thead>
                    <tr>
                      <th>Grade</th>
                      <th>Threshold</th>
                      <th>Target</th>
                      <th>Stretch</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="4" className="text-center">
                        No data available
                      </td>
                    </tr>
                  </tbody>
                </Table>
              );
            }

            // Map dataset label to its data array for easy lookup
            const datasetMap = {};
            stiBaseTypeAvg.datasets.forEach((ds) => {
              datasetMap[ds.label] = ds.data;
            });

            // Get all unique dataset labels in the order: Threshold, Target, Stretch (if present)
            const allLabels = stiBaseTypeAvg.datasets.map((ds) => ds.label);
            const orderedLabels = ["Threshold", "Target", "Stretch"].filter((lbl) =>
              allLabels.includes(lbl)
            );

            return (
              <Table striped bordered hover responsive className="mt-3 mb-4">
                <thead>
                  <tr>
                    <th>Grade</th>
                    {orderedLabels.map((lbl) => (
                      <th key={lbl}>{lbl}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stiBaseTypeAvg.labels.map((grade, idx) => (
                    <tr key={grade}>
                      <td>{grade}</td>
                      {orderedLabels.map((lbl) => {
                        const value = datasetMap[lbl]?.[idx];
                        // Show as integer, rounded up, or "-" if not present
                        return (
                          <td key={lbl}>
                            {typeof value === "number" && !isNaN(value)
                              ? Math.ceil(value) + "%"
                              : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            );
          })()}
            </>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          <h5>STI plan types by grade</h5>
          <p>
            The chart below shows the distribution of STI plan types by grade. Each bar represents the percentage of participants for each plan type within a grade.
          </p>
          {reportData.chartDataByGradingSystem?.stiPlanTypesPerformanceConditions && (
            <div style={{ height: "500px", marginBottom: "2rem" }}>
              <Bar
                data={{
                  ...reportData.chartDataByGradingSystem.stiPlanTypesPerformanceConditions,
                  datasets: reportData.chartDataByGradingSystem.stiPlanTypesPerformanceConditions.datasets.map(
                    (dataset, index) => ({
                      ...dataset,
                      backgroundColor:
                        stiPlanTypesPerformanceConditionsColors
                          ? stiPlanTypesPerformanceConditionsColors[index % stiPlanTypesPerformanceConditionsColors.length]
                          : undefined,
                      borderColor:
                        stiPlanTypesPerformanceConditionsColors
                          ? stiPlanTypesPerformanceConditionsColors[index % stiPlanTypesPerformanceConditionsColors.length]
                          : undefined,
                      borderWidth: 1,
                    })
                  ),
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "x",
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: "Grades",
                      },
                      stacked: false,
                    },
                    y: {
                      title: {
                        display: true,
                        text: "Percentage of Participants",
                      },
                      beginAtZero: true,
                      max: 100,
                    },
                  },
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                    datalabels: {
                      formatter: (value) => (value > 0 ? value + "%" : ""),
                      color: "#fff",
                      font: {
                        weight: "bold",
                      },
                    },
                  },
                }}
              />
            </div>
          )}


          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* STI Performance Assessment
           * This section provides detailed information on the STI performance assessment for Short-Term Incentive (STI) plans
           * across different job grades. The data is presented in a chart showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5>STI performance measurement criteria</h5>
          <p>
          Short-Term Incentive (STI) outcomes usually depend on company, team, and individual performance. The chart below shows their relative impact.  
          </p>

          {reportData.chartDataByGradingSystem.stiPerformanceAssessment ? (
            <div style={{ height: "500px", margin: "2rem 0", width: "100%" }}>
              <Bar
                data={{
                  ...reportData.chartDataByGradingSystem
                    .stiPerformanceAssessment,
                  datasets:
                    reportData.chartDataByGradingSystem.stiPerformanceAssessment.datasets.map(
                      (dataset, index) => ({
                        ...dataset,
                        backgroundColor:
                          stiPerformanceAssessmentColors[
                            index % stiPerformanceAssessmentColors.length
                          ],
                        borderColor:
                          stiPerformanceAssessmentColors[
                            index % stiPerformanceAssessmentColors.length
                          ],
                        borderWidth: 1,
                      })
                    ),
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      stacked: true,
                      title: {
                        display: true,
                        text: "Grade",
                      },
                    },
                    y: {
                      stacked: true,
                      title: {
                        display: true,
                        text: "Percentage (%)",
                      },
                      max: 100,
                    },
                  },
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        boxWidth: 12,
                        generateLabels: (chart) => {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => ({
                            text: formatLegendLabel(dataset.label || ""),
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.borderColor,
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                          }));
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return `${formatLegendLabel(context.dataset.label)}: ${context.raw}%`;
                        },
                      },
                    },
                    datalabels: {
                      color: "#FFFFFF",
                      font: {
                        weight: "bold",
                        size: 9,
                      },
                      formatter: function (value) {
                        return value > 0 ? value + "%" : "";
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="text-center my-4">
              No performance assessment data available
            </div>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          {/* Short-term Incentives performance conditions
           * This section provides detailed information on the short-term incentive performance conditions for Short-Term Incentive (STI) plans
           * across different job grades. The data is presented in a chart showing the percentage of companies that use the expected value methodology versus those that use face value methodology when making allocations.
           *
           */}
          <h5>STI performance conditions</h5>
          <p>
          The graph below displays the performance conditions typically linked to STI plans and the percentage of participants who reported using them. 
          </p>
          {reportData.chartDataByGradingSystem?.stiPerformanceMeasures ? (
            <div style={{ height: "500px", margin: "2rem 0", width: "100%" }}>
              <Bar
                data={{
                  ...reportData.chartDataByGradingSystem.stiPerformanceMeasures,
                  datasets:
                    reportData.chartDataByGradingSystem.stiPerformanceMeasures.datasets.map(
                      (dataset, index) => ({
                        ...dataset,
                        backgroundColor: dataset.data.map(
                          (_, i) =>
                            ltiPerformanceConditionsColors[
                              i % ltiPerformanceConditionsColors.length
                            ]
                        ),
                        borderColor: dataset.data.map(
                          (_, i) =>
                            ltiPerformanceConditionsColors[
                              i % ltiPerformanceConditionsColors.length
                            ]
                        ),
                        borderWidth: 1,
                      })
                    ),
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "x",
                  scales: {
                    y: {
                      title: {
                        display: true,
                        text: "Percentage (%)",
                      },
                      max: 100,
                    },
                    x: {
                      title: {
                        display: true,
                        text: "Performance Measure",
                      },
                    },
                  },
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return `${context.raw}%`;
                        },
                      },
                    },
                    datalabels: {
                      color: "#FFFFFF",
                      font: {
                        weight: "bold",
                        size: 9,
                      },
                      anchor: "end",
                      align: "start",
                      formatter: function (value) {
                        return value > 0 ? value + "%" : "";
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="text-center my-4">
              No STI performance measures data available
            </div>
          )}

          <hr
            className="section-divider my-4"
            style={{ borderTop: "2px solid #dee2e6" }}
          />

          <h5>Performance modifiers</h5>
          <p>
          The table below presents the STI metrics that participants reported using as modifiers.  
          </p>
          {reportData.chartDataByGradingSystem?.stiPerformanceModifiers ? (
            <div style={{ margin: "2rem 0" }}>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Performance level</th>
                    <th>Performance modifier</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.chartDataByGradingSystem.stiPerformanceModifiers.labels.map(
                    (label, index) => (
                      <tr key={`performance-modifier-${label}-${index}`}>
                        <td>{label}</td>
                        <td>
                          {
                            reportData.chartDataByGradingSystem
                              .stiPerformanceModifiers.datasets[0].data[index]
                          }
                          %
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center my-4">
              No STI performance modifiers data available
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>

      {!hiddenSections.totalRemunerationPayMixOldVersion && (
      <Accordion.Item eventKey="3">
        <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
          TOTAL REMUNERATION PAY MIX
        </Accordion.Header>
        <Accordion.Body>
          <h5>Total remuneration pay mix</h5>

          {(() => {
            // Defensive: check data exists
            const stiEligibility = reportData.chartDataByGradingSystem.stiEligibility;
            const stiTarget = reportData.chartDataByGradingSystem.stiTargetAnnualReward;
            const ltiEligibility = reportData.chartDataByGradingSystem.ltiEligibility;
            const ltiTarget = reportData.chartDataByGradingSystem.ltiTargetAnnualReward;

            // Helper to get grades with eligibility (None != 100)
            function getEligibleGrades(eligibility) {
              if (
                eligibility &&
                Array.isArray(eligibility.labels) &&
                Array.isArray(eligibility.datasets)
              ) {
                const noneIdx = eligibility.datasets.findIndex(
                  ds => ds.label && ds.label.toLowerCase().includes("none")
                );
                if (noneIdx !== -1) {
                  return eligibility.labels.filter((grade, i) => {
                    const val = eligibility.datasets[noneIdx].data[i];
                    return Number(val) !== 100;
                  });
                }
              }
              return [];
            }

            // Helper to get grades present in a dataset
            function getGradesPresent(target) {
              return new Set(target?.labels || []);
            }

            // STI
            const stiEligibleGrades = getEligibleGrades(stiEligibility);
            const stiPresentGrades = getGradesPresent(stiTarget);
            const stiMissingGrades = stiEligibleGrades.filter(
              grade => !stiPresentGrades.has(grade)
            );

            // LTI
            const ltiEligibleGrades = getEligibleGrades(ltiEligibility);
            const ltiPresentGrades = getGradesPresent(ltiTarget);
            const ltiMissingGrades = ltiEligibleGrades.filter(
              grade => !ltiPresentGrades.has(grade)
            );

            if (stiMissingGrades.length > 0 || ltiMissingGrades.length > 0) {
              return (
                <div className="alert alert-warning my-2" style={{ fontSize: "0.95em" }}>
                  <strong>Note:</strong>{" "}
                  {stiMissingGrades.length > 0 && (
                    <>
                      The following grades have STI eligibility but are not shown in the STI tables below due to insufficient STI data:{" "}
                      <span style={{ fontWeight: 700 }}>
                        {stiMissingGrades.join(", ")}
                      </span>
                      <br />
                    </>
                  )}
                  {ltiMissingGrades.length > 0 && (
                    <>
                      The following grades have LTI eligibility but are not shown in the LTI tables below due to insufficient LTI data:{" "}
                      <span style={{ fontWeight: 700 }}>
                        {ltiMissingGrades.join(", ")}
                      </span>
                    </>
                  )}
                </div>
              );
            }
            return null;
          })()}

          
          <p>
          The table below illustrates the market median (50th percentile) based percentages of Total Guaranteed Package (TGP), Short-Term Incentives (STI), and Long-Term Incentives (LTI), which together constitute the total remuneration mix for each grade.
          </p>
          {reportData.chartDataByGradingSystem?.stiTargetAnnualReward &&
          reportData.chartDataByGradingSystem?.ltiTargetAnnualReward ? (
            <div style={{ height: "500px", margin: "2rem 0", width: "100%" }}>
              <Bar
                data={(() => {
                  // Defensive: check data exists
                  const stiData =
                    reportData.chartDataByGradingSystem.stiTargetAnnualReward;
                  const ltiData =
                    reportData.chartDataByGradingSystem.ltiTargetAnnualReward;
                  if (
                    !stiData ||
                    !stiData.labels ||
                    !stiData.datasets ||
                    stiData.labels.length === 0 ||
                    !ltiData ||
                    !ltiData.labels ||
                    !ltiData.datasets ||
                    ltiData.labels.length === 0
                  ) {
                    return { labels: [], datasets: [] };
                  }

                  // Helper to find dataset index by label substring
                  function findDatasetIndex(datasets, labelSubstring) {
                    return datasets.findIndex(ds =>
                      ds.label && ds.label.toLowerCase().includes(labelSubstring.toLowerCase())
                    );
                  }

                  // Find TGP Average and Base Salary P50 for both STI and LTI
                  const stiTgpAvgIdx = findDatasetIndex(stiData.datasets, "TGP Average");
                  const stiBaseP50Idx = findDatasetIndex(stiData.datasets, "Base Salary P50");
                  const ltiTgpAvgIdx = findDatasetIndex(ltiData.datasets, "TGP Average");
                  const ltiBaseP50Idx = findDatasetIndex(ltiData.datasets, "Base Salary P50");

                  // Use all unique grades from both STI and LTI
                  const uniqueGradesSet = new Set([
                    ...stiData.labels,
                    ...ltiData.labels,
                  ]);
                  // Sort by grade name (alphanumeric)
                  const uniqueGrades = Array.from(uniqueGradesSet).sort((a, b) =>
                    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
                  );

                  const tgpData = [];
                  const stiDataArr = [];
                  const ltiDataArr = [];

                  uniqueGrades.forEach((grade) => {
                    // STI values
                    let stiTgp = null, stiValue = null;
                    const stiIdx = stiData.labels.indexOf(grade);
                    if (stiIdx !== -1 && stiTgpAvgIdx !== -1 && stiBaseP50Idx !== -1) {
                      stiTgp = Number(stiData.datasets[stiTgpAvgIdx].data[stiIdx]);
                      stiValue = Number(stiData.datasets[stiBaseP50Idx].data[stiIdx]);
                      if (isNaN(stiTgp)) stiTgp = null;
                      if (isNaN(stiValue)) stiValue = null;
                    }

                    // LTI values
                    let ltiTgp = null, ltiValue = null;
                    const ltiIdx = ltiData.labels.indexOf(grade);
                    if (ltiIdx !== -1 && ltiTgpAvgIdx !== -1 && ltiBaseP50Idx !== -1) {
                      ltiTgp = Number(ltiData.datasets[ltiTgpAvgIdx].data[ltiIdx]);
                      ltiValue = Number(ltiData.datasets[ltiBaseP50Idx].data[ltiIdx]);
                      if (isNaN(ltiTgp)) ltiTgp = null;
                      if (isNaN(ltiValue)) ltiValue = null;
                    }

                    // Use TGP from STI if available, else from LTI, else 0
                    let tgp = stiTgp !== null ? stiTgp : (ltiTgp !== null ? ltiTgp : 0);

                    // Calculate STI % of TGP
                    let stiPct = null;
                    if (stiTgp !== null && stiValue !== null && stiTgp > 0) {
                      stiPct = (stiValue / stiTgp) * 100;
                    }

                    // Calculate LTI % of TGP
                    let ltiPct = null;
                    if (ltiTgp !== null && ltiValue !== null) {
                      // Use TGP from STI if available and > 0, else from LTI
                      let tgpForLti = stiTgp !== null && stiTgp > 0 ? stiTgp : ltiTgp;
                      if (tgpForLti > 0) {
                        ltiPct = (ltiValue / tgpForLti) * 100;
                      }
                    }

                    // TGP is always 100% of itself (raw)
                    const tgpPctRaw = 100;

                    // If both STI and LTI are missing, just show TGP as 100%
                    let tgpScaled = 100, stiScaled = 0, ltiScaled = 0;
                    if (
                      (typeof stiPct === "number" && !isNaN(stiPct) && stiPct > 0) ||
                      (typeof ltiPct === "number" && !isNaN(ltiPct) && ltiPct > 0)
                    ) {
                      // If either STI or LTI is present, scale all three so their sum is 100%
                      const totalRaw = tgpPctRaw + (stiPct || 0) + (ltiPct || 0);
                      if (totalRaw > 0) {
                        tgpScaled = (tgpPctRaw / totalRaw) * 100;
                        stiScaled = ((stiPct || 0) / totalRaw) * 100;
                        ltiScaled = ((ltiPct || 0) / totalRaw) * 100;
                      }
                    }

                    tgpData.push(Math.round(tgpScaled));
                    stiDataArr.push(Math.round(stiScaled));
                    ltiDataArr.push(Math.round(ltiScaled));
                  });

                  return {
                    labels: uniqueGrades,
                    datasets: [
                      {
                        label: "TGP",
                        data: tgpData,
                        backgroundColor: REMCHANNEL_COLORS.steelBlue,
                        stack: "Stack 0",
                      },
                      {
                        label: "On-target STI",
                        data: stiDataArr,
                        backgroundColor: REMCHANNEL_COLORS.green,
                        stack: "Stack 0",
                      },
                      {
                        label: "On-target LTI",
                        data: ltiDataArr,
                        backgroundColor: REMCHANNEL_COLORS.orange,
                        stack: "Stack 0",
                      },
                    ],
                  };
                })()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      stacked: true,
                      title: {
                        display: true,
                        text: "Grade",
                      },
                    },
                    y: {
                      stacked: true,
                      title: {
                        display: true,
                        text: "Percentage of Total Remuneration",
                      },
                      ticks: {
                        callback: function (value) {
                          return value + "%";
                        },
                      },
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return (
                            formatLegendLabel(context.dataset.label) + ": " + context.raw + "%"
                          );
                        },
                      },
                    },
                    legend: {
                      position: "bottom",
                      labels: {
                        boxWidth: 12,
                        generateLabels: (chart) => {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => ({
                            text: formatLegendLabel(dataset.label || ""),
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.borderColor,
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                          }));
                        },
                      },
                    },
                    datalabels: {
                      formatter: (value) => {
                        return value > 0 ? value + "%" : "";
                      },
                      color: "#fff",
                      font: {
                        weight: "bold",
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="text-center my-4">No pay mix data available</div>
          )}

          <h5>Total Remuneration pay mix for each grade. </h5>
         
          <Table striped bordered hover responsive className="mt-3">
            <thead>
              <tr>
                <th style={{ width: "120px" }}>Grade</th>
                <th style={{ width: "120px" }}>TGP %</th>
                <th style={{ width: "120px" }}>On-target STI</th>
                <th style={{ width: "120px" }}>On-target LTI</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const stiData =
                  reportData.chartDataByGradingSystem?.stiTargetAnnualReward;
                const ltiData =
                  reportData.chartDataByGradingSystem?.ltiTargetAnnualReward;

                if (
                  !stiData?.labels ||
                  !stiData?.datasets ||
                  stiData.labels.length === 0 ||
                  !ltiData?.labels ||
                  !ltiData?.datasets ||
                  ltiData.labels.length === 0
                ) {
                  return (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No data available
                      </td>
                    </tr>
                  );
                }

                // Helper to find dataset index by label substring
                function findDatasetIndex(datasets, labelSubstring) {
                  return datasets.findIndex(ds =>
                    ds.label && ds.label.toLowerCase().includes(labelSubstring.toLowerCase())
                  );
                }

                // Find TGP Average and Base Salary P50 for both STI and LTI
                const stiTgpAvgIdx = findDatasetIndex(stiData.datasets, "TGP Average");
                const stiBaseP50Idx = findDatasetIndex(stiData.datasets, "Base Salary P50");
                const ltiTgpAvgIdx = findDatasetIndex(ltiData.datasets, "TGP Average");
                const ltiBaseP50Idx = findDatasetIndex(ltiData.datasets, "Base Salary P50");

                // Use all unique grades from both STI and LTI, sorted by grade name
                const uniqueGradesSet = new Set([
                  ...stiData.labels,
                  ...ltiData.labels,
                ]);
                const uniqueGrades = Array.from(uniqueGradesSet).sort((a, b) =>
                  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
                );

                return uniqueGrades.map((grade) => {
                  // STI values
                  let stiTgp = null, stiValue = null;
                  const stiIdx = stiData.labels.indexOf(grade);
                  if (stiIdx !== -1 && stiTgpAvgIdx !== -1 && stiBaseP50Idx !== -1) {
                    stiTgp = Number(stiData.datasets[stiTgpAvgIdx].data[stiIdx]) || 0;
                    stiValue = Number(stiData.datasets[stiBaseP50Idx].data[stiIdx]) || 0;
                  }

                  // LTI values
                  let ltiTgp = null, ltiValue = null;
                  const ltiIdx = ltiData.labels.indexOf(grade);
                  if (ltiIdx !== -1 && ltiTgpAvgIdx !== -1 && ltiBaseP50Idx !== -1) {
                    ltiTgp = Number(ltiData.datasets[ltiTgpAvgIdx].data[ltiIdx]) || 0;
                    ltiValue = Number(ltiData.datasets[ltiBaseP50Idx].data[ltiIdx]) || 0;
                  }

                  // Use TGP from STI if available, else from LTI, else 0
                  let tgp = stiTgp !== null ? stiTgp : (ltiTgp !== null ? ltiTgp : 0);

                  // Calculate STI % of TGP
                  let stiPct = 0;
                  if (stiTgp && stiValue) {
                    stiPct = (stiValue / stiTgp) * 100;
                  }

                  // Calculate LTI % of TGP
                  let ltiPct = 0;
                  if (ltiTgp && ltiValue) {
                    // Use TGP from LTI if STI TGP not available
                    let tgpForLti = stiTgp !== null ? stiTgp : ltiTgp;
                    if (tgpForLti) {
                      ltiPct = (ltiValue / tgpForLti) * 100;
                    }
                  }

                  // If both STI and LTI are missing, just show TGP as 100%
                  let tgpScaled = 100, stiScaled = 0, ltiScaled = 0;
                  if (
                    (typeof stiPct === "number" && !isNaN(stiPct) && stiPct > 0) ||
                    (typeof ltiPct === "number" && !isNaN(ltiPct) && ltiPct > 0)
                  ) {
                    // If either STI or LTI is present, scale all three so their sum is 100%
                    const tgpPctRaw = 100;
                    const totalRaw = tgpPctRaw + (stiPct || 0) + (ltiPct || 0);
                    if (totalRaw > 0) {
                      tgpScaled = (tgpPctRaw / totalRaw) * 100;
                      stiScaled = ((stiPct || 0) / totalRaw) * 100;
                      ltiScaled = ((ltiPct || 0) / totalRaw) * 100;
                    }
                  }

                  // Round for display
                  tgpScaled = Math.round(tgpScaled);
                  stiScaled = Math.round(stiScaled);
                  ltiScaled = Math.round(ltiScaled);

                  return (
                    <tr key={`pay-mix-${grade}`}>
                      <td>{grade}</td>
                      <td>{tgpScaled}%</td>
                      <td>{stiScaled}%</td>
                      <td>{ltiScaled}%</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>
      )}

      {reportData.dataAugmentedPayMix && (
        <Accordion.Item eventKey="4">
          <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
            LTI Data Collection Pay Mix
          </Accordion.Header>
          <Accordion.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5>LTI Data Collection Pay Mix Percentiles by {circleName || 'Circle'}</h5>
                <p className="text-muted mb-0">
                  Percentiles calculated from TGP, LTI Amount, On Target LTI Amount, STI Amount, and On Target STI Amount values in Remchannel for all organizations in the circle.
                  The requesting organization's data is excluded from calculations.
                </p>
              </div>
              {false && hasAllowedRole && (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={async () => {
                      try {
                        setSyncingData(true);
                        const orgIdToUse = organizationId || orgId;
                        if (!orgIdToUse) {
                          alert('Organization ID is required to sync data');
                          return;
                        }
                        const result = await triggerDataSync(orgIdToUse);
                        alert(`Data sync job queued successfully. Job ID: ${result.jobId}. The sync may take a few minutes. You can recalculate percentiles after the sync completes.`);
                      } catch (error) {
                        console.error('Error syncing data:', error);
                        alert(`Failed to sync data: ${error.response?.data?.message || error.message || 'Unknown error'}`);
                      } finally {
                        setSyncingData(false);
                      }
                    }}
                    disabled={syncingData || !organizationId}
                    title="Sync employee data from MSSQL to PostgreSQL"
                  >
                    {syncingData ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Sync Data
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={async () => {
                      try {
                        setRecalculatingPercentiles(true);
                        const reportIdToUse = reportId || reportData?.metadata?.reportId || 'unknown';
                        const result = await recalculatePercentiles(reportIdToUse);
                        
                        // Refresh the report data if callback is provided
                        if (onReportRefresh) {
                          await onReportRefresh();
                        } else {
                          // Fallback: show success message and suggest page refresh
                          const message = result.validation?.isValid 
                            ? `Percentiles recalculated successfully. ${result.percentileCount || 0} percentile groups calculated. Please refresh the page to see the updated data.`
                            : `Percentiles recalculated with warnings: ${result.validation?.reasons?.join('; ') || 'Unknown issues'}. Please refresh the page to see the updated data.`;
                          alert(message);
                        }
                      } catch (error) {
                        console.error('Error recalculating percentiles:', error);
                        alert(`Failed to recalculate percentiles: ${error.message}`);
                      } finally {
                        setRecalculatingPercentiles(false);
                      }
                    }}
                    disabled={recalculatingPercentiles || !reportId || !reportData?.dataAugmentedPayMix}
                  >
                    {recalculatingPercentiles ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Recalculating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Recalculate Percentiles
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Conditions for percentile data to appear - only show for KAM users */}
            {hasAllowedRole && (
              <Alert variant="info" className="mb-3">
                <strong>Conditions for Percentile Data to Appear:</strong>
                <ul className="mb-0 mt-2">
                  <li>The "Data Augmented Pay Mix" checkbox must be enabled when creating the report</li>
                  <li><strong>Employee data must be synced from MSSQL to PostgreSQL</strong> - Data from UploadedEmployees (MSSQL) and SnapshotEmployeeData (MSSQL) must be synced to the <code>uploaded_employees</code> table (PostgreSQL). This sync happens automatically daily at 7 AM. <strong>Note:</strong> If PostgreSQL data is not available, the system will automatically fall back to calculating percentiles directly from MSSQL tables, but this will not include user-editable LTI amounts.</li>
                  <li>At least one organization (other than the requesting organization) must exist in the circle</li>
                  <li>Synced data must have <strong>grade_name populated</strong> (this is critical - data without grade_name will be excluded). The grade_name comes from SnapshotEmployeeData during the sync process.</li>
                  <li>Data must include <strong>at least one</strong> of: TGP, LTI Amount, On Target LTI Amount, STI, or On Target STI values (you don't need all fields - TGP and STI alone are sufficient). STI values come from SnapshotEmployeeData (FB17, FB40) during sync.</li>
                  <li>grade_name values must match valid Paterson Classic grade names in the system</li>
                  <li>Sufficient data points per grade are required for meaningful percentile calculations (minimum 10 data points and 3 organizations per field for percentiles)</li>
                </ul>
                <p className="mb-0 mt-2"><strong>Note:</strong> Percentiles are calculated independently for each field. If you have TGP and STI data but no LTI data, TGP and STI percentiles will still be calculated.</p>
              </Alert>
            )}
            
            {!reportData.dataAugmentedPayMixPercentiles || (Array.isArray(reportData.dataAugmentedPayMixPercentiles) && reportData.dataAugmentedPayMixPercentiles.length === 0) ? (
              <Alert variant="warning" className="mt-3">
                <strong>No percentile data available.</strong>
                <p className="mb-0 mt-2">
                  Percentile calculations may not have completed or there may be insufficient data. 
                  Please check the report generation logs or try regenerating the report with the "Data Augmented Pay Mix" option enabled.
                </p>
                {reportData.dataAugmentationValidation && !reportData.dataAugmentationValidation.isValid && (
                  <div className="mt-2">
                    <strong>Validation Issues:</strong>
                    <ul className="mb-0">
                      {reportData.dataAugmentationValidation.reasons?.map((reason, idx) => {
                        // Check if this is a sync-related issue
                        const isSyncIssue = reason && (
                          reason.includes('No employee data found in PostgreSQL') ||
                          reason.includes('must be synced from MSSQL') ||
                          reason.includes('Data must be synced')
                        );
                        // Check if this is the light load message
                        const isLightLoad = reason && reason.includes('light load');
                        return (
                          <li key={idx}>
                            {isSyncIssue ? (
                              <>
                                <strong style={{ color: '#d32f2f' }}>⚠️ {reason}</strong>
                                <br />
                                <small className="text-muted">
                                  To sync data: Go to the <strong>LTI Data Collection</strong> screen and click the <strong>"Sync Data"</strong> button. 
                                  Data syncs automatically daily at 7 AM, but you can trigger a manual sync at any time.
                                </small>
                              </>
                            ) : isLightLoad ? (
                              <span style={{ color: '#ff9800' }}>ℹ️ {reason}</span>
                            ) : (
                              reason
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </Alert>
            ) : (
              <>

            {/* Show validation info box if data is insufficient */}
            {(() => {
              // Check config from reportData - default to true (show even with insufficient data)
              const showWithInsufficientData = reportData.showDataAugmentationWithInsufficientData !== false;
              const hasInsufficientData = reportData.dataAugmentationValidation && !reportData.dataAugmentationValidation.isValid;
              
              return (
                <>
                  {hasInsufficientData && (
                    <Alert variant="info" className="mb-4">
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}>
                        — denotes insufficient sample in the methodology.
                      </p>
                      <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                        Please contact your Key Accounts Manager (KAM) for assistance.
                      </p>
                    </Alert>
                  )}
                  
                  {/* Show tables if validation passes OR if env var allows showing with insufficient data */}
                  {(!hasInsufficientData || showWithInsufficientData) && reportData.dataAugmentedPayMixPercentiles.length === 0 ? (
                    <div className="text-center my-4">No percentile data available</div>
                  ) : (!hasInsufficientData || showWithInsufficientData) ? (
              (() => {
                const fields = [
                  { key: 'tgp', label: 'TGP' },
                  { key: 'ltiAmount', label: 'LTI Amount' },
                  { key: 'ontargetLtiAmount', label: 'On Target LTI Amount' },
                  { key: 'sti', label: 'STI Amount' },
                  { key: 'onTargetSti', label: 'On Target STI Amount' }
                ];

                const formatCurrency = (value) => {
                  if (value === null || value === undefined) return '—';
                  if (typeof value === 'number') {
                    return value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 });
                  }
                  return '—';
                };

                return (
                  <div>
                    {hasAllowedRole && (
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={async () => {
                          try {
                            setExportingPercentileData(true);
                            const reportIdToUse = reportId || reportData?.metadata?.reportId || 'unknown';
                            const circleIdToUse = circleId || reportData?.circleId;
                            const circleNameToUse = reportData?.circleName || 'National';
                            const gradingSystemIdToUse = gradingSystemId || reportData?.gradingSystemId;
                            const orgIdToUse = organizationId || orgId || reportData?.metadata?.organizationId;
                            
                            await exportPercentileSourceDataToExcel(
                              reportIdToUse,
                              circleIdToUse,
                              circleNameToUse,
                              gradingSystemIdToUse,
                              orgIdToUse
                            );
                          } catch (error) {
                            console.error('Error exporting percentile source data:', error);
                            alert(`Failed to export percentile source data: ${error.message}`);
                          } finally {
                            setExportingPercentileData(false);
                          }
                        }}
                        disabled={exportingPercentileData || !reportData?.metadata}
                      >
                        {exportingPercentileData ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-earmark-excel me-2"></i>
                            Download Source Data
                          </>
                        )}
                      </Button>
                    </div>
                    )}
                    {fields.map((field) => {
                      // Check if any grade has data for this field
                      const hasData = reportData.dataAugmentedPayMixPercentiles.some(
                        (gradeData) => (gradeData[`${field.key}_count`] || 0) > 0
                      );
                      
                      if (!hasData) return null;
                      
                      return (
                        <div key={field.key} className="mb-5">
                          <h6 className="mb-3">{field.label} Percentiles by Survey Grade</h6>
                          <Table striped bordered hover responsive className="mt-3">
                            <thead>
                              <tr>
                                <th>Survey Grade</th>
                                <th>Market Sample</th>
                                <th>Percentage Received</th>
                                <th>10th Percentile</th>
                                <th>25th Percentile</th>
                                <th>50th Percentile (Median)</th>
                                <th>75th Percentile</th>
                                <th>90th Percentile</th>
                                <th>Mean</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.dataAugmentedPayMixPercentiles
                                .filter((gradeData) => (gradeData[`${field.key}_count`] || 0) > 0)
                                .map((gradeData, index) => (
                                  <tr key={index}>
                                    <td><strong>{gradeData.grade || '—'}</strong></td>
                                    <td>
                                      {gradeData[`${field.key}_count`] || 0}
                                      {hasAllowedRole && (gradeData.grade != null && gradeData.grade !== '') && (
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          className="ms-2"
                                          disabled={exportingGradeCsv !== null}
                                          onClick={async () => {
                                            const grade = gradeData.grade || '';
                                            setExportingGradeCsv(grade);
                                            try {
                                              await downloadPercentileSourceDataCsvForGrade(reportId, circleId, organizationId, grade, field.key);
                                            } catch (err) {
                                              console.error('Error downloading CSV for grade:', err);
                                              alert(`Failed to download CSV: ${err.message || err}`);
                                            } finally {
                                              setExportingGradeCsv(null);
                                            }
                                          }}
                                          title={`Download CSV for grade ${gradeData.grade || '—'}`}
                                        >
                                          {exportingGradeCsv === (gradeData.grade || '') ? (
                                            <Spinner animation="border" size="sm" />
                                          ) : (
                                            <i className="bi bi-download" />
                                          )}
                                        </Button>
                                      )}
                                    </td>
                                    <td>{gradeData[`${field.key}_percentageReceived`] != null && gradeData[`${field.key}_percentageReceived`] !== undefined ? `${Number(gradeData[`${field.key}_percentageReceived`]).toFixed(1)}%` : '—'}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile10`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile25`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile50`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile75`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_percentile90`])}</td>
                                    <td>{formatCurrency(gradeData[`${field.key}_mean`])}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
                  ) : null}
                </>
              );
            })()}

            {/* Pay Mix Stacked Bar Chart - show if validation passes OR if config allows showing with insufficient data */}
            {(() => {
              const showWithInsufficientData = reportData.showDataAugmentationWithInsufficientData !== false;
              const hasInsufficientData = reportData.dataAugmentationValidation && !reportData.dataAugmentationValidation.isValid;
              const shouldShowChart = (!hasInsufficientData || showWithInsufficientData) && reportData.dataAugmentedPayMixPercentiles && reportData.dataAugmentedPayMixPercentiles.length > 0;
              
              return shouldShowChart ? (() => {
              // Prepare chart data - use 50th percentile (median) values for each grade
              const chartData = reportData.dataAugmentedPayMixPercentiles
                .filter((gradeData) => {
                  // Only include grades that have at least one of the displayed fields with data
                  return (gradeData.tgp_count || 0) > 0 || 
                         (gradeData.ontargetLtiAmount_count || 0) > 0 || 
                         (gradeData.onTargetSti_count || 0) > 0;
                })
                .map((gradeData) => {
                  // Use 50th percentile (median) values, defaulting to 0 if not available
                  return {
                    grade: gradeData.grade || '—',
                    tgp: gradeData.tgp_percentile50 || 0,
                    ontargetLtiAmount: gradeData.ontargetLtiAmount_percentile50 || 0,
                    onTargetSti: gradeData.onTargetSti_percentile50 || 0,
                  };
                })
                .sort((a, b) => {
                  // Sort by grade (numeric if possible, otherwise string)
                  const aNum = Number(a.grade);
                  const bNum = Number(b.grade);
                  if (!isNaN(aNum) && !isNaN(bNum)) {
                    return aNum - bNum;
                  }
                  return String(a.grade).localeCompare(String(b.grade));
                });

              if (chartData.length === 0) {
                return null;
              }

              const chartConfig = {
                type: 'bar',
                data: {
                  labels: chartData.map(d => d.grade),
                  datasets: [
                    {
                      label: 'TGP',
                      data: chartData.map(d => d.tgp),
                      backgroundColor: REMCHANNEL_COLORS.steelBlue,
                      borderColor: REMCHANNEL_COLORS.steelBlue,
                      borderWidth: 1,
                    },
                    {
                      label: 'On Target LTI Amount',
                      data: chartData.map(d => d.ontargetLtiAmount),
                      backgroundColor: REMCHANNEL_COLORS.green60,
                      borderColor: REMCHANNEL_COLORS.green60,
                      borderWidth: 1,
                    },
                    {
                      label: 'On Target STI Amount',
                      data: chartData.map(d => d.onTargetSti),
                      backgroundColor: REMCHANNEL_COLORS.rust,
                      borderColor: REMCHANNEL_COLORS.rust,
                      borderWidth: 1,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Pay Mix by Survey Grade (50th Percentile / Median)',
                      font: {
                        size: 16,
                        weight: 'bold',
                      },
                      padding: {
                        top: 10,
                        bottom: 20,
                      },
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: {
                          size: 12,
                        },
                        generateLabels: (chart) => {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => ({
                            text: formatLegendLabel(dataset.label ?? ""),
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.borderColor,
                            lineWidth: dataset.borderWidth ?? 1,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                          }));
                        },
                      },
                    },
                    tooltip: {
                      enabled: true,
                      callbacks: {
                        label: function(context) {
                          const value = context.parsed.y;
                          return `${formatLegendLabel(context.dataset.label)}: ${value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 })}`;
                        },
                      },
                    },
                    datalabels: {
                      display: false,
                    },
                  },
                  scales: {
                    x: {
                      stacked: true,
                      title: {
                        display: true,
                        text: 'Grade',
                        font: {
                          size: 12,
                          weight: 'bold',
                        },
                      },
                      ticks: {
                        font: {
                          size: 11,
                        },
                      },
                    },
                    y: {
                      stacked: true,
                      title: {
                        display: true,
                        text: 'Amount (ZAR)',
                        font: {
                          size: 12,
                          weight: 'bold',
                        },
                      },
                      ticks: {
                        callback: function(value) {
                          return value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
                        },
                        font: {
                          size: 11,
                        },
                      },
                    },
                  },
                },
              };

              return (
                <div className="mt-5">
                  <h6 className="mb-3">Pay Mix Stacked Bar Chart</h6>
                  <div style={{ height: '500px', position: 'relative' }}>
                    <Bar data={chartConfig.data} options={chartConfig.options} />
                  </div>
                </div>
              );
            })() : null;
            })()}
              </>
            )}
          </Accordion.Body>
        </Accordion.Item>
      )}
    </Accordion>
  );
};

export default ReportSections;
